import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  getVitalSigns,
  getEnalysisReport,
  getEnalysisModules,
  getEnalysisFrames,
  getEnalysisWarningFlags,
} from "@/api/enalysis-api.js";
import { useDataLoaderContext } from "./DataLoaderContext.jsx";
import { useError } from "./ErrorContext.jsx";
import { shouldReportApiError } from "@/api/api-utils.js";
import { useAssets } from "./AssetContext.jsx";
import { useSessionTenant } from "./SessionTenantContext.jsx";

/** @param {unknown} err */
function errorDetail(err) {
  if (err == null) return undefined;
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message || String(err);
  if (typeof err === "object" && "message" in err && err.message != null)
    return String(err.message);
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

/**
 * @typedef {Object} EnalysisCacheEntry
 * @property {Object|null} vitalSigns
 * @property {Object|null} report
 * @property {Object|null} fieldData - `report.fieldData`; merged after report, before VitalSigns for operating conditions
 * @property {Object|null} modules
 * @property {Object|null} frames
 * @property {Object|null} warningFlags
 * @property {string|null} vitalSignsError
 * @property {boolean} vitalSignsLoading
 * @property {boolean} operatingConditionsLoading
 * @property {boolean} equipmentInfoLoading
 */

/**
 * @typedef {Object} EnalysisOptimizationContextType
 * @property {(compNum: number|string, compId: number|string) => EnalysisCacheEntry} getOptimizationData
 * @property {(compNum: number|string, compId: number|string) => Promise<void>} fetchOptimizationData
 * @property {(compNum: number|string, compId: number|string) => void} clearCache
 * @property {() => void} clearAllCache
 * @property {number} dataRefreshToken - Increments with DataLoaderProvider (default 15 min); use in effects to refetch after cache invalidation
 */

const EMPTY_ENTRY = {
  vitalSigns: null,
  report: null,
  fieldData: null,
  modules: null,
  frames: null,
  vitalSignsError: null,
  vitalSignsLoading: false,
  operatingConditionsLoading: false,
  equipmentInfoLoading: false,
};

/** fieldData is copied from report.fieldData; do not require it for cache completeness. */
const CACHE_KEYS_REQUIRED = [
  "vitalSigns",
  "report",
  "modules",
  "frames",
  "warningFlags",
];

const cacheKey = (compNum, compId) => `${compNum}-${compId}`;
const emptyEntry = () => ({ ...EMPTY_ENTRY });

/** Loading flags set when starting or invalidating a cache entry (stale-while-revalidate on interval). */
const ENTRY_LOADING = {
  vitalSignsError: null,
  vitalSignsLoading: true,
  operatingConditionsLoading: true,
  equipmentInfoLoading: true,
};

const EnalysisOptimizationContext = createContext(
  /** @type {EnalysisOptimizationContextType|null} */ (null)
);

export const useEnalysisOptimizationContext = () => {
  const context = useContext(EnalysisOptimizationContext);
  if (!context) {
    throw new Error(
      "useEnalysisOptimizationContext must be used within EnalysisOptimizationProvider"
    );
  }
  return context;
};

/** Caches Enalysis optimization data; refreshes on the shared data-loader interval after fleet assets finish loading. */
export function EnalysisOptimizationProvider({ children }) {
  const { triggerCount } = useDataLoaderContext();
  const { tenantRefreshKey } = useSessionTenant();
  const { isLoading: assetsLoading } = useAssets();
  const { addError } = useError();
  const cacheRef = useRef(
    /** @type {Map<string, EnalysisCacheEntry>} */ (new Map())
  );
  const [, setCacheVersion] = useState(0);
  const [dataRefreshToken, setDataRefreshToken] = useState(0);
  const inFlightRef = useRef(/** @type {Set<string>} */ (new Set()));
  const lastTriggerRef = useRef(-1);
  const pendingAfterFleetRef = useRef(false);

  const updateCache = useCallback((updater) => {
    const newCache = updater(cacheRef.current);
    cacheRef.current = newCache;
    setCacheVersion((v) => v + 1);
  }, []);

  const getOptimizationData = useCallback((compNum, compId) => {
    if (compNum == null || compId == null) return emptyEntry();
    return cacheRef.current.get(cacheKey(compNum, compId)) ?? emptyEntry();
  }, []);

  const fetchOptimizationData = useCallback(
    async (compNum, compId) => {
      if (compNum == null || compId == null) return;
      const key = cacheKey(compNum, compId);
      const existing = cacheRef.current.get(key);
      const hasAll =
        existing && CACHE_KEYS_REQUIRED.every((k) => existing[k] != null);
      if (hasAll && !existing.vitalSignsLoading) return;

      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);

      updateCache((cache) => {
        const next = new Map(cache);
        const entry = next.get(key) ?? emptyEntry();
        next.set(key, { ...entry, ...ENTRY_LOADING });
        return next;
      });

      const setEntry = (patch) => {
        updateCache((cache) => {
          const next = new Map(cache);
          next.set(key, { ...(next.get(key) ?? emptyEntry()), ...patch });
          return next;
        });
      };

      const vitalSignsPromise = getVitalSigns(compNum, compId)
        .then((data) =>
          setEntry({
            vitalSigns: data,
            vitalSignsLoading: false,
            vitalSignsError: null,
          })
        )
        .catch((err) =>
          setEntry({
            vitalSignsLoading: false,
            vitalSignsError: err?.message ?? "Failed to load VitalSigns",
          })
        );

      const operatingConditionsPromise = Promise.all([
        getEnalysisReport(compNum, compId),
        getEnalysisWarningFlags(compNum, compId),
      ])
        .catch((err) => {
          if (err?.response?.status === 404) {
            return [null, null];
          }
          if (shouldReportApiError(err)) {
            addError(
              "Failed to load Enalysis report and warning flags",
              errorDetail(err)
            );
          }
          return null;
        })
        .then(([report, warningFlags]) => {
          setEntry({
            report: report,
            fieldData: report?.fieldData ?? null,
            warningFlags: warningFlags,
            operatingConditionsLoading: false,
          });
        });

      const equipmentInfoPromise = Promise.all([
        getEnalysisModules(compNum, compId).catch((err) => {
          if (shouldReportApiError(err)) {
            addError("Failed to load Enalysis modules", errorDetail(err));
          }
          return null;
        }),
        getEnalysisFrames(compNum, compId).catch((err) => {
          if (shouldReportApiError(err)) {
            addError("Failed to load Enalysis frames", errorDetail(err));
          }
          return null;
        }),
      ]).then(([modules, frames]) =>
        setEntry({
          modules: modules,
          frames: frames,
          equipmentInfoLoading: false,
        })
      );

      Promise.allSettled([
        vitalSignsPromise,
        operatingConditionsPromise,
        equipmentInfoPromise,
      ]).finally(() => inFlightRef.current.delete(key));
    },
    [updateCache, addError]
  );

  const clearCache = useCallback(
    (compNum, compId) => {
      if (compNum == null || compId == null) return;
      const key = cacheKey(compNum, compId);
      inFlightRef.current.delete(key);
      updateCache((cache) => {
        const next = new Map(cache);
        next.delete(key);
        return next;
      });
    },
    [updateCache]
  );

  const clearAllCache = useCallback(() => {
    inFlightRef.current.clear();
    updateCache(() => new Map());
  }, [updateCache]);

  useEffect(() => {
    if (tenantRefreshKey > 0) {
      clearAllCache();
      setDataRefreshToken((n) => n + 1);
    }
  }, [tenantRefreshKey, clearAllCache]);

  useEffect(() => {
    const t = triggerCount;
    if (t > lastTriggerRef.current && t > 0)
      pendingAfterFleetRef.current = true;
    lastTriggerRef.current = t;

    if (!pendingAfterFleetRef.current || assetsLoading) return;
    pendingAfterFleetRef.current = false;

    inFlightRef.current.clear();
    if (cacheRef.current.size > 0) {
      updateCache((cache) => {
        const next = new Map();
        for (const [k, entry] of cache) {
          next.set(k, { ...entry, ...ENTRY_LOADING });
        }
        return next;
      });
    }
    setDataRefreshToken((n) => n + 1);
  }, [triggerCount, assetsLoading, updateCache]);

  const value = {
    getOptimizationData,
    fetchOptimizationData,
    clearCache,
    clearAllCache,
    dataRefreshToken,
  };

  return (
    <EnalysisOptimizationContext.Provider value={value}>
      {children}
    </EnalysisOptimizationContext.Provider>
  );
}
